import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  ButtonGroup,
  VStack,
  HStack,
  Box,
  Text,
  Input,
  FormControl,
  FormLabel,
  Divider,
  useColorModeValue,
  useToast,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFilePowerpoint, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { ColorPresets } from './ColorPresets';
import { ExportPreview } from './ExportPreview';
import { generatePDF, generatePowerPoint } from './exportUtils';
import {
  DEFAULT_EXPORT_CONFIG,
  PAGE_DEFAULTS,
  loadExportConfig,
  saveExportConfig,
} from './exportDefaults';

export const ExportBuilderModal = ({
  isOpen,
  onClose,
  pageType = 'reports',
  data = {},
  dateRange = 'today',
}) => {
  const [config, setConfig] = useState(loadExportConfig());
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  // Load config on open
  useEffect(() => {
    if (isOpen) {
      setConfig(loadExportConfig());
    }
  }, [isOpen]);

  // Auto-save config when it changes
  useEffect(() => {
    if (isOpen) {
      saveExportConfig(config);
    }
  }, [config, isOpen]);

  const updateConfig = (path, value) => {
    setConfig((prev) => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const handleReset = () => {
    setConfig(DEFAULT_EXPORT_CONFIG);
    saveExportConfig(DEFAULT_EXPORT_CONFIG);
    toast({ title: 'Settings reset to defaults', status: 'info', duration: 2000 });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      if (config.exportType === 'pdf') {
        await generatePDF({ config, data, pageType, dateRange, toast });
      } else {
        await generatePowerPoint({ config, data, pageType, dateRange, toast });
      }
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const pageDefaults = PAGE_DEFAULTS[pageType] || PAGE_DEFAULTS.reports;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg={bgColor} borderRadius="2xl" mx={4} maxH="90vh" overflow="hidden">
        <ModalHeader borderBottom="1px solid" borderColor={borderColor}>
          Export Report
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6} overflow="auto">
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
            {/* Left Column - Controls */}
            <GridItem>
              <VStack spacing={5} align="stretch">
                {/* Format Selection */}
                <Box>
                  <Text fontSize="sm" fontWeight="500" color={labelColor} mb={2}>
                    Format
                  </Text>
                  <ButtonGroup size="sm" isAttached variant="outline" w="100%">
                    <Button
                      flex={1}
                      leftIcon={<FontAwesomeIcon icon={faFilePdf} />}
                      colorScheme={config.exportType === 'pdf' ? 'brand' : 'gray'}
                      variant={config.exportType === 'pdf' ? 'solid' : 'outline'}
                      onClick={() => updateConfig('exportType', 'pdf')}
                      borderRadius="xl"
                    >
                      PDF
                    </Button>
                    <Button
                      flex={1}
                      leftIcon={<FontAwesomeIcon icon={faFilePowerpoint} />}
                      colorScheme={config.exportType === 'pptx' ? 'brand' : 'gray'}
                      variant={config.exportType === 'pptx' ? 'solid' : 'outline'}
                      onClick={() => updateConfig('exportType', 'pptx')}
                      borderRadius="xl"
                    >
                      PowerPoint
                    </Button>
                  </ButtonGroup>
                </Box>

                <Divider />

                {/* Colors Section */}
                <Box>
                  <Text fontSize="sm" fontWeight="600" color={labelColor} mb={3} textTransform="uppercase" letterSpacing="wide">
                    Colors
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <ColorPresets
                      label="Primary Color"
                      type="primary"
                      selectedColor={config.colors.primary}
                      onSelect={(color) => updateConfig('colors.primary', color)}
                    />
                    <ColorPresets
                      label="Accent Color"
                      type="accent"
                      selectedColor={config.colors.accent}
                      onSelect={(color) => updateConfig('colors.accent', color)}
                    />
                  </VStack>
                </Box>

                <Divider />

                {/* Branding Section */}
                <Box>
                  <Text fontSize="sm" fontWeight="600" color={labelColor} mb={3} textTransform="uppercase" letterSpacing="wide">
                    Branding
                  </Text>
                  <FormControl>
                    <FormLabel fontSize="sm" color={labelColor}>Company Name</FormLabel>
                    <Input
                      size="sm"
                      borderRadius="lg"
                      value={config.branding.companyName}
                      onChange={(e) => updateConfig('branding.companyName', e.target.value)}
                      placeholder="StatFalcon"
                    />
                  </FormControl>
                </Box>

                <Divider />

                {/* Text Section */}
                <Box>
                  <Text fontSize="sm" fontWeight="600" color={labelColor} mb={3} textTransform="uppercase" letterSpacing="wide">
                    Text
                  </Text>
                  <VStack spacing={3} align="stretch">
                    <FormControl>
                      <FormLabel fontSize="sm" color={labelColor}>Title</FormLabel>
                      <Input
                        size="sm"
                        borderRadius="lg"
                        value={config.text.title}
                        onChange={(e) => updateConfig('text.title', e.target.value)}
                        placeholder={pageDefaults.title}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color={labelColor}>Subtitle</FormLabel>
                      <Input
                        size="sm"
                        borderRadius="lg"
                        value={config.text.subtitle}
                        onChange={(e) => updateConfig('text.subtitle', e.target.value)}
                        placeholder={pageDefaults.subtitle}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color={labelColor}>Footer Text</FormLabel>
                      <Input
                        size="sm"
                        borderRadius="lg"
                        value={config.text.footerText}
                        onChange={(e) => updateConfig('text.footerText', e.target.value)}
                        placeholder={`Generated by ${config.branding.companyName || 'StatFalcon'}`}
                      />
                    </FormControl>
                  </VStack>
                </Box>
              </VStack>
            </GridItem>

            {/* Right Column - Preview */}
            <GridItem>
              <ExportPreview config={config} pageType={pageType} />
            </GridItem>
          </Grid>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={borderColor} gap={3}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<FontAwesomeIcon icon={faRotateLeft} />}
            onClick={handleReset}
            borderRadius="xl"
          >
            Reset Defaults
          </Button>
          <HStack spacing={3} ml="auto">
            <Button variant="ghost" onClick={onClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleExport}
              isLoading={isExporting}
              loadingText="Exporting..."
              borderRadius="xl"
              leftIcon={<FontAwesomeIcon icon={config.exportType === 'pdf' ? faFilePdf : faFilePowerpoint} />}
            >
              Export {config.exportType === 'pdf' ? 'PDF' : 'PowerPoint'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
